import {
  AfterViewInit,
  Component,
  computed,
  effect,
  HostListener,
  inject,
  LOCALE_ID,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  ActivatedRoute,
  NavigationEnd,
  Router,
  RouterOutlet,
} from '@angular/router';
import { PrimeNG } from 'primeng/config';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DialogService, DynamicDialogModule } from 'primeng/dynamicdialog';
import { ToastModule } from 'primeng/toast';
import { take } from 'rxjs';
import { AppStoreBanner } from './components/app-store-banner/app-store-banner';
import { DayCompletedComponent } from './components/day-completed/day-completed';
import { ImpersonationBanner } from './components/impersonation-banner/impersonation-banner';
import { Sidebar } from './components/sidebar/sidebar';
import { ClaimAccountDialog } from './dialogs/claim-account-dialog/claim-account-dialog';
import { RecordingPreferenceDialog } from './dialogs/recording-preference/recording-preference';
import { hr } from './hr';
import { UserRole } from './models/user.model';
import { EntitlementService } from './services/entitlement.service';
import { ImpersonationService } from './services/impersonation.service';
import { ShellPathService } from './services/shell-path.service';
import { TimeTrackingService } from './services/time-tracking.service';
import { UserService } from './services/user';
import { UserAudioService } from './services/user-audio.service';
import { PracticeOnboardingChecklist } from './components/practice-onboarding-checklist/practice-onboarding-checklist';
import { PracticeOnboardingService } from './services/practice-onboarding.service';

@Component({
  selector: 'app-root',
  imports: [
    RouterOutlet,
    ToastModule,
    ConfirmDialogModule,
    DynamicDialogModule,
    Sidebar,
    DayCompletedComponent,
    AppStoreBanner,
    ImpersonationBanner,
    PracticeOnboardingChecklist,
  ],
  templateUrl: './app.html',
  styleUrl: './app.css',
  standalone: true,
})
export class App implements AfterViewInit, OnInit {
  protected readonly title = signal('pronunciation');
  protected readonly impersonationService = inject(ImpersonationService);
  protected readonly userService = inject(UserService);
  private readonly practiceOnboardingService = inject(PracticeOnboardingService);

  private practiceOnboardingLoadedForUserId = signal<number | null>(null);
  protected readonly userAudioService = inject(UserAudioService);
  protected readonly shellService = inject(ShellPathService);
  protected readonly entitlementService = inject(EntitlementService);
  protected readonly route = inject(ActivatedRoute);
  private readonly dialogService = inject(DialogService);

  private readonly primeNG = inject(PrimeNG);
  private readonly localeId = inject(LOCALE_ID);

  collapsed = signal(false);
  showSidebar = signal(false);
  hideSidebar = signal(false);
  showWelcomeHeader = signal(false);
  showDarkModeToggle = signal(false);
  hideWelcomeCard = signal(false);
  /** Set to true after opacity transition (300ms) finishes when hiding the welcome card. */
  welcomeCardDisplayNone = signal(false);

  /** True while recording preference dialog is open (prevents duplicate opens) */
  private recordingPreferenceDialogOpen = signal(false);
  private claimAccountDialogOpen = signal(false);
  private entitlementsLoadedForUserId = signal<number | null>(null);

  private authPages = ['/signin', '/signup', '/verify-email'];
  private publicPages = ['/agb', '/privacy-policy', '/delete-account'];
  private router = inject(Router);
  private timeTrackingService = inject(TimeTrackingService);
  private welcomeCardHideTimeout: ReturnType<typeof setTimeout> | null = null;
  isExerciseRoute = signal(false);

  protected impersonationLabel = computed(() => {
    const user = this.userService.currentUser();
    if (!user || !this.impersonationService.isImpersonating()) return '';
    return `${user.firstName} ${user.lastName}`.trim() || user.username;
  });

  constructor() {
    effect(() => {
      const user = this.userService.currentUser();
      if (!user) {
        this.entitlementsLoadedForUserId.set(null);
        this.entitlementService.clearEntitlements();
        return;
      }

      if (
        this.entitlementsLoadedForUserId() === user.id &&
        this.entitlementService.entitlements()
      ) {
        return;
      }

      this.entitlementsLoadedForUserId.set(user.id);
      this.entitlementService.refreshEntitlements().pipe(take(1)).subscribe();
    });

    effect(() => {
      const user = this.userService.currentUser();
      if (!user?.practice?.id || !this.userService.isPracticeOwner()) {
        this.practiceOnboardingLoadedForUserId.set(null);
        return;
      }

      if (
        this.practiceOnboardingLoadedForUserId() === user.id &&
        this.practiceOnboardingService.isLoaded()
      ) {
        return;
      }

      this.practiceOnboardingLoadedForUserId.set(user.id);
      this.practiceOnboardingService.load().pipe(take(1)).subscribe();
    });

    effect(() => {
      const user = this.userService.currentUser();
      const isClaimPending = this.entitlementService.isClaimPending();

      if (
        !user ||
        !isClaimPending ||
        this.claimAccountDialogOpen() ||
        this.impersonationService.isImpersonating()
      ) {
        return;
      }

      this.claimAccountDialogOpen.set(true);
      this.dialogService
        .open(ClaimAccountDialog, {
          header: $localize`Claim your account`,
          modal: true,
          closable: false,
          width: '500px',
          breakpoints: { '640px': '95vw' },
        })
        ?.onClose.pipe(take(1))
        .subscribe(() => {
          this.claimAccountDialogOpen.set(false);
        });
    });

    effect(() => {
      const user = this.userService.currentUser();
      const hasNoRecordingMode =
        user &&
        (user.recordingMode === null || user.recordingMode === undefined);

      const isPatient = user?.role === UserRole.PATIENT;
      if (
        isPatient &&
        hasNoRecordingMode &&
        !this.recordingPreferenceDialogOpen() &&
        !this.entitlementService.isClaimPending()
      ) {
        this.recordingPreferenceDialogOpen.set(true);
        this.dialogService
          .open(RecordingPreferenceDialog, {
            header: $localize`Choose your recording method`,
            modal: true,
            closable: false,
            width: '500px',
            breakpoints: { '640px': '95vw' },
          })
          ?.onClose.subscribe(() => {
            this.recordingPreferenceDialogOpen.set(false);
          });
      }
    });
    effect(() => {
      const hide = this.hideWelcomeCard();
      if (this.welcomeCardHideTimeout) {
        clearTimeout(this.welcomeCardHideTimeout);
        this.welcomeCardHideTimeout = null;
      }
      if (hide) {
        this.welcomeCardHideTimeout = setTimeout(() => {
          this.welcomeCardDisplayNone.set(true);
          this.welcomeCardHideTimeout = null;
        }, 300);
      } else {
        this.welcomeCardDisplayNone.set(false);
      }
    });
    this.route.url.pipe(takeUntilDestroyed()).subscribe((urlSegments) => {
      if (urlSegments.length > 0 && urlSegments[0].path === 'app') {
        this.hideSidebar.set(true);
      } else {
        this.hideSidebar.set(false);
      }
    });
    this.router.events.pipe(takeUntilDestroyed()).subscribe((event) => {
      if (event instanceof NavigationEnd) {
        const user = this.userService.currentUser();
        const needsOnboarding = this.needsUserOnboarding(user);
        if (needsOnboarding && !this.isOnboardingRouteActive()) {
          this.shellService.navigateToRoute(['onboarding']);
          return;
        }
        if (!needsOnboarding && this.isOnboardingRouteActive()) {
          this.shellService.navigateToRoute(['']);
          return;
        }

        // only use up to the query params
        if (
          (event.urlAfterRedirects === '/app' ||
            event.urlAfterRedirects === '/') &&
          !this.userService.isPracticeEmployee()
        ) {
          this.showWelcomeHeader.set(true);
        } else {
          this.showWelcomeHeader.set(false);
        }
        if (event.urlAfterRedirects.includes('app')) {
          this.hideSidebar.set(true);
        } else {
          this.hideSidebar.set(false);
        }
        const url = event.urlAfterRedirects.split('?')[0];
        this.showSidebar.set(
          !this.authPages.includes(url) && !this.publicPages.includes(url),
        );
        this.showDarkModeToggle.set(this.authPages.includes(url));
        this.isExerciseRoute.set(
          this.timeTrackingService.isExerciseRoute(this.router.url),
        );
      }
    });
  }

  ngOnInit(): void {
    if (
      typeof this.localeId === 'string' &&
      this.localeId.toLowerCase().startsWith('hr')
    ) {
      this.primeNG.setTranslation(hr);
    }
  }

  @HostListener('window:scroll', ['$event'])
  protected onWindowScroll(event: Event): void {
    if (window.scrollY > 50) {
      this.hideWelcomeCard.set(true);
    } else if (window.scrollY < 10) {
      this.hideWelcomeCard.set(false);
    }
  }

  ngAfterViewInit(): void {
    // get collapsed state from local storage
    this.collapsed.set(
      localStorage.getItem('sidebarCollapsed') === 'true' ? true : false,
    );
  }

  toggleSidebar() {
    this.collapsed.update((v) => !v);
    // store collapsed state in local storage
    localStorage.setItem('sidebarCollapsed', this.collapsed().toString());
  }

  private needsUserOnboarding(
    user: ReturnType<UserService['currentUser']>,
  ): boolean {
    if (!user) return false;
    const isRegularUser = user.role === UserRole.USER;
    const isNonAdmin = user.isAdmin === false;
    const onboardingCompleted = user.onboardingCompleted === true;

    return isRegularUser && isNonAdmin && !onboardingCompleted;
  }

  private isOnboardingRouteActive(): boolean {
    return this.router.url.includes('onboarding');
  }
}
